import { ApiFragment, Family } from '@stylehaven/components';

export class FragmentPlaybackPlugin {
    private readonly player: any;
    private readonly mediaRecord: ApiFragment;

    private readonly FPS: number = 25;
    private readonly offset: number;
    private readonly duration: number;
    private durationInSeconds: number;

    private readonly startFrame: number;
    private readonly endFrame: number;
    private readonly startTimeInSeconds: number;
    private readonly endTimeInSeconds: number;

    constructor(player: any, mediaRecord: ApiFragment) {
        this.player = player;
        this.mediaRecord = mediaRecord;

        this.offset = mediaRecord.get(Family.Technical, 'StartFrames') ?? 0; // Start of main fragment
        this.duration = (mediaRecord.get(Family.Technical, 'EndFrames') ?? 0) - this.offset; // Duration of main fragment

        this.startFrame = this.mediaRecord.get(Family.Structural, 'FragmentStartFrames'); // Start of this fragment
        this.endFrame = this.mediaRecord.get(Family.Structural, 'FragmentEndFrames'); // End of this fragment

        this.startTimeInSeconds = this.frameToSeconds(this.startFrame);
        this.endTimeInSeconds = this.frameToSeconds(this.endFrame);
    }

    public initPlugin(): void {
        this.player.ready(() => {
            this.player.on('loadedmetadata', () => {
                this.durationInSeconds = this.player.duration(); // Need to wait for the player to be ready to set this property

                this.setFragmentStart();
                this.initHighlight();
                this.initPlayerEventListeners();
            });
        });
    }

    // #region Set start point
    private setFragmentStart(): void {
        if (this.startTimeInSeconds > 0) {
            this.player.currentTime(this.startTimeInSeconds);

            this.preciseUpdateListener();
        }
    }
    // #endregion

    // #region Set highlight
    private initHighlight(): void {
        // Add div and css of highlight (more accurate using time instead of frames)
        const left = this.startTimeInSeconds / this.durationInSeconds;
        const width = (this.endFrame - this.startFrame) / this.FPS / this.durationInSeconds;
        const clss = 'vjs-marker';

        const highlightDiv = document.createElement('DIV');
        highlightDiv.className = clss;
        highlightDiv.style.height = '3px';
        highlightDiv.style.width = width * 100 + '%';
        highlightDiv.style.left = left * 100 + '%';
        highlightDiv.style.backgroundColor = '#f8e520';
        highlightDiv.style.position = 'absolute';

        this.progressBar.appendChild(highlightDiv);
    }
    private get progressBar(): any {
        return this.player.controlBar.progressControl.el();
    }
    // #endregion

    // #region Playback time events
    private initPlayerEventListeners(): void {
        this.player.on('timeupdate', () => this.onTimeUpdate());
    }

    private onTimeUpdate(): void {
        const currentTime = this.player.currentTime();

        if (currentTime >= this.endTimeInSeconds) {
            this.player.pause();
            this.player.currentTime(this.endTimeInSeconds);
            this.changePlayToRestartIcon();
        }

        if (currentTime < this.startTimeInSeconds) {
            this.player.currentTime(this.startTimeInSeconds);
            this.player.play();
        }
    }

    /**
     * Same as onTimeUpdate but more accurate (and more resource intensive)
     * Only runs once and only intended to be used for very short fragments
     */
    private preciseUpdateListener(): void {
        if (this.durationInSeconds < 5) {
            setTimeout(() => {
                if (this.player.currentTime() > this.endTimeInSeconds - 0.05) {
                    this.player.pause();
                    this.player.currentTime(this.endTimeInSeconds);
                }
            }, this.durationInSeconds * 1000);
        }
    }

    private changePlayToRestartIcon(): void {
        const button = document.getElementsByClassName('vjs-play-control')[0];
        button.classList.add('vjs-play-control', 'vjs-control', 'vjs-button', 'vjs-paused', 'vjs-ended');

        button.addEventListener('mousedown', event => this.onRestartClick());
    }

    private onRestartClick(): void {
        const currentTime = this.player.currentTime();

        // Force restart if reached end of fragment or skipping to point outside of fragment
        if (currentTime < this.startTimeInSeconds || currentTime >= this.endTimeInSeconds) {
            this.replayFragment();
        }
    }

    private replayFragment(): void {
        this.player.currentTime(this.startTimeInSeconds);

        this.preciseUpdateListener();
    }

    // #endregion

    // #region Helpers
    private frameToSeconds(frame: number): number {
        const end: number = this.player.duration() - 1 / this.FPS;
        // Offset not needed in this calculation since we already defined that the offset is the start of the video
        let seconds: number = frame / this.FPS;

        if (seconds > end) {
            seconds = end;
        }

        return seconds;
    }
    // #endregion
}
